import { useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { TextField, useMediaQuery } from '@mui/material';
import { breakpoints } from '../../constants';
import { Row } from '../Row';
import { TFormValues } from '../../types';

interface IProps {
    formValues: TFormValues;
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    setFieldValue(field: keyof TFormValues, value: string): void;
}

interface IPostcodeRequest {
    zipCode: string;
    houseNumber: string;
}

interface IPostcodeResponse {
    city: string;
    street: string;
}

export const RelocationAddressSection = ({
    formValues,
    getFieldError,
    setFieldValue,
}: IProps) => {
    const [zipcodeError, setZipcodeError] = useState(false);
    const mediumBreakpoint = useMediaQuery(`(${breakpoints.mediumMin})`);

    const handleHouseNumberBlur = () => {
        if (
            !process.env.REACT_APP_POSTCODE_API_URL ||
            !formValues.newZipcode ||
            !formValues.newHouseNumber
        )
            return;

        axios
            .post<IPostcodeRequest, AxiosResponse<IPostcodeResponse>>(
                process.env.REACT_APP_POSTCODE_API_URL,
                {
                    zipCode: formValues.newZipcode,
                    houseNumber: formValues.newHouseNumber,
                }
            )
            .then((res) => {
                const { street, city } = res.data;

                setFieldValue('newStreet', street);
                setFieldValue('newCity', city);
            });
    };

    const handleZipcodeBlur = () => {
        if (formValues.newZipcode === '') return setZipcodeError(false);

        const formattedZipcode = formValues.newZipcode
            .replace(/\s/g, '')
            .toUpperCase();

        setFieldValue('newZipcode', formattedZipcode);

        // 1234AB
        const zipcodeRegex = /^\d{4}[A-Z]{2}$/;

        if (!formattedZipcode.match(zipcodeRegex)) {
            setZipcodeError(true);

            return;
        }

        setZipcodeError(false);
    };

    const getZipcodeHelperText = () => {
        if (getFieldError('newZipcode')) return getFieldError('newZipcode');
        if (zipcodeError) return 'Geen geldige postcode';

        return '';
    };

    return (
        <>
            <Row>
                <TextField
                    error={!!getFieldError('newZipcode') || zipcodeError}
                    helperText={getZipcodeHelperText()}
                    id={formValues.newZipcode}
                    label="Postcode *"
                    placeholder="1234AB"
                    value={formValues.newZipcode}
                    onBlur={handleZipcodeBlur}
                    onChange={(e) =>
                        setFieldValue('newZipcode', e.target.value)
                    }
                />
                <TextField
                    error={!!getFieldError('newHouseNumber')}
                    helperText={getFieldError('newHouseNumber')}
                    id={formValues.newHouseNumber}
                    label="Huisnummer *"
                    sx={{ margin: `${mediumBreakpoint && '0 10px'}` }}
                    value={formValues.newHouseNumber}
                    onBlur={handleHouseNumberBlur}
                    onChange={(e) =>
                        setFieldValue('newHouseNumber', e.target.value)
                    }
                />
                <TextField
                    error={!!getFieldError('newHouseNumberAddition')}
                    helperText={getFieldError('newHouseNumberAddition')}
                    id={formValues.newHouseNumberAddition}
                    label="Toevoeging"
                    value={formValues.newHouseNumberAddition}
                    onChange={(e) =>
                        setFieldValue('newHouseNumberAddition', e.target.value)
                    }
                />
            </Row>
            <TextField
                error={!!getFieldError('newStreet')}
                helperText={getFieldError('newStreet')}
                id={formValues.newStreet}
                label="Straat *"
                value={formValues.newStreet}
                onChange={(e) => setFieldValue('newStreet', e.target.value)}
            />
            <TextField
                error={!!getFieldError('newCity')}
                helperText={getFieldError('newCity')}
                id={formValues.newCity}
                label="Plaats *"
                value={formValues.newCity}
                onChange={(e) => setFieldValue('newCity', e.target.value)}
            />
        </>
    );
};
